/**
 * alerts-board.tsx — the ONE alerts state the ops console reads (quality register Q1/Q2,
 * 30 October 2026).
 *
 * ⭐ WHY ONE SOURCE. The navbar indicator polled the summary every 60 s; the Alerts page read
 * the list once when it opened and never again; the Home widget read the summary once. On
 * 30 October the overnight sign-up alert cleared itself, the navbar caught it and showed 1, and
 * the open Alerts page still showed 2. Three readers on three clocks will always disagree at
 * some moment. Here there is one reader: every 60 s, when the tab becomes visible again, and
 * straight after any acknowledge or clear, it fans out `listAlerts` to every zone, and the
 * navbar count, the Critical card and the Critical pill are all computed by `boardCounts` from
 * that one list — so they are the same number by construction.
 *
 * ⭐ NO SILENT UNDERCOUNT. A zone that does not answer contributes nothing, and a lower number
 * reads exactly like a calmer platform. `failedZones` travels with the counts so every surface
 * that shows a count can say it is partial and name the zone (Q2).
 *
 * Polls only for roles that can read alerts (alerts:read — super_admin, nexa_admin, support):
 * for any other role every zone would refuse, and the navbar would show a permanent "did not
 * answer" for a board that person was never meant to see.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useStaffMe } from '../hooks/useStaffMe';
import { listAlerts, boardCounts, type Alert, type BoardCounts } from './alerts-api';

/** How often the board re-reads every zone. */
export const ALERTS_REFRESH_MS = 60_000;

export interface AlertsBoardState {
  /** `idle` — not polling (signed out, or a role that cannot read alerts). */
  status: 'idle' | 'loading' | 'loaded';
  /** Every alert on the board, every zone, critical-first, zone-tagged. */
  alerts: Alert[];
  /** Labels of zones that did not answer the last read — render this, never drop it. */
  failedZones: string[];
  /** When the last read finished (ISO-8601), or null before the first one. */
  updatedAt: string | null;
  /** Counts over `alerts` — the one place the navbar, the card and the pill take their numbers. */
  counts: BoardCounts;
  /** Re-read now (after an acknowledge or clear). Resolves when the read has landed. */
  refresh: () => Promise<void>;
}

const EMPTY = boardCounts([]);

const AlertsBoardContext = createContext<AlertsBoardState | null>(null);

/** Roles whose session can read the board (mirrors alerts:read in the Cedar policy). */
export const ALERTS_READ_ROLES = new Set(['super_admin', 'nexa_admin', 'support']);

export function AlertsBoardProvider({ children }: { children: ReactNode }) {
  const { status: authStatus } = useAuth();
  const me = useStaffMe();
  const role = me.status === 'loaded' ? me.me.role : undefined;
  const enabled = authStatus === 'authenticated' && role !== undefined && ALERTS_READ_ROLES.has(role);

  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [failedZones, setFailedZones] = useState<string[]>([]);
  const [updatedAt, setUpdatedAt] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);
  // A read that started before a newer one must not land after it and roll the board back.
  const seq = useRef(0);

  const refresh = useCallback(async () => {
    const mine = ++seq.current;
    try {
      const r = await listAlerts();
      if (mine !== seq.current) return;
      setAlerts(r.alerts);
      setFailedZones(r.failedZones);
    } catch {
      // `listAlerts` settles every zone, so a throw here means nothing was read at all: keep the
      // last board and say every zone is unanswered rather than showing a confident zero.
      if (mine !== seq.current) return;
      setFailedZones(['every zone']);
    }
    setUpdatedAt(new Date().toISOString());
    setLoaded(true);
  }, []);

  useEffect(() => {
    if (!enabled) return;
    void refresh();
    const id = setInterval(() => void refresh(), ALERTS_REFRESH_MS);
    const onVisible = () => {
      if (document.visibilityState === 'visible') void refresh();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [enabled, refresh]);

  const counts = useMemo(() => boardCounts(alerts), [alerts]);
  const value = useMemo<AlertsBoardState>(
    () => ({
      status: !enabled ? 'idle' : loaded ? 'loaded' : 'loading',
      alerts,
      failedZones,
      updatedAt,
      counts,
      refresh,
    }),
    [enabled, loaded, alerts, failedZones, updatedAt, counts, refresh],
  );

  return <AlertsBoardContext.Provider value={value}>{children}</AlertsBoardContext.Provider>;
}

/** The shared alerts board. Outside a provider it reads as idle and empty (never throws). */
export function useAlertsBoard(): AlertsBoardState {
  return (
    useContext(AlertsBoardContext) ?? {
      status: 'idle',
      alerts: [],
      failedZones: [],
      updatedAt: null,
      counts: EMPTY,
      refresh: async () => undefined,
    }
  );
}
